import { useEffect, useRef } from 'react'
import { Animated } from 'react-native'

/**
 * Hook personnalisé pour gérer le cleanup des animations React Native
 * Évite le warning "Sending `onAnimatedValueUpdate` with no listeners registered"
 */
export const useAnimationCleanup = (animatedValues: Animated.Value[]) => {
  const animationsRef = useRef<Animated.CompositeAnimation[]>([])

  // Fonction pour enregistrer une animation
  const registerAnimation = (animation: Animated.CompositeAnimation) => {
    animationsRef.current.push(animation)
    return animation
  }

  // Cleanup au démontage du composant
  useEffect(() => {
    return () => {
      // Arrêter toutes les animations enregistrées
      animationsRef.current.forEach(animation => {
        animation.stop()
      })
      
      // Arrêter toutes les valeurs animées
      animatedValues.forEach(value => {
        value.stopAnimation()
      })
      
      // Nettoyer la liste des animations
      animationsRef.current = []
    }
  }, [animatedValues])

  return { registerAnimation }
}

/**
 * Hook simplifié pour une seule valeur animée
 */
export const useAnimatedValue = (initialValue: number = 0) => {
  const animatedValue = useRef(new Animated.Value(initialValue)).current
  const { registerAnimation } = useAnimationCleanup([animatedValue])

  return { animatedValue, registerAnimation }
}

/**
 * Hook pour plusieurs valeurs animées
 */
export const useAnimatedValues = (initialValues: number[]) => {
  const animatedValues = useRef(
    initialValues.map(value => new Animated.Value(value))
  ).current
  const { registerAnimation } = useAnimationCleanup(animatedValues)

  return { animatedValues, registerAnimation }
} 